import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import ReactModal from "react-modal";
import { getEmailLogById, resendEmail } from "../../../server/server";
import { notif } from "../../utils/util";
import ConfirmationModal from "../../modals/ConfirmationModal";

interface EmailLogDetail {
  id: string;
  to: string;
  from: string;
  subject: string;
  status: string;
  purpose: string;
  sentAt: string;
  sentBy: number | null;
  resendFromId: string | null;
  errorMessage: string | null;
  body: string;
  smtpServer: {
    id: string;
    name: string;
    host: string;
    [key: string]: any;
  } | null;
  resentFrom: {
    id: string;
    to: string;
    subject: string;
    sentAt: string;
    [key: string]: any;
  } | null;
}

const PURPOSE_LABELS: Record<string, string> = {
  registration: "Регистрација",
  password_reset: "Ресет лозинке",
  contact: "Контакт",
  chat_notifications: "Обавештења ћаскања",
  fallback: "Резервно",
};

const formatDateTime = (iso: string) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("sr-Latn-RS", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
};

interface Props {
  logId: string;
  isOpen: boolean;
  onClose: () => void;
}

const EmailLogDetailModal: React.FC<Props> = ({ logId, isOpen, onClose }) => {
  const [log, setLog] = useState<EmailLogDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    ReactModal.setAppElement("#root");
  }, []);

  useEffect(() => {
    if (isOpen && logId) {
      setLoading(true);
      getEmailLogById(logId).then((resp) => {
        if (resp.status === 200) {
          setLog(resp.data);
        } else {
          notif("Грешка при учитавању детаља", "error");
        }
        setLoading(false);
      });
    }
  }, [isOpen, logId]);

  const handleResend = async () => {
    if (!log) return;
    setResending(true);
    const resp = await resendEmail(log.id);
    if (resp.status === 200 || resp.status === 201) {
      notif("Е-порука је успешно поново послата", "success");
    } else {
      notif("Грешка при поновном слању е-поруке", "error");
    }
    setResending(false);
    setResendConfirmOpen(false);
  };

  const infoRow = (label: string, value: React.ReactNode) => (
    <div className="grid grid-cols-[180px_1fr] gap-2 py-2 border-b border-[#81848f3d] last:border-b-0">
      <span className="font-semibold text-gray-600 text-sm">{label}</span>
      <span className="text-sm text-gray-900">{value || "—"}</span>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        contentLabel="Детаљи е-поште"
        style={{
          overlay: { zIndex: 1500 },
          content: {
            width: "90%",
            maxWidth: 900,
            maxHeight: "90vh",
            padding: 0,
            margin: "auto",
            border: "none",
            boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
            overflow: "hidden",
          },
        }}
      >
        <div className="flex flex-col h-full max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-[#04438b] text-white">
            <h2 className="text-xl font-bold">Детаљи е-поруке</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300 text-2xl font-bold leading-none"
            >
              ×
            </button>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center py-16 text-gray-500">
              Учитавање…
            </div>
          ) : log ? (
            <div className="flex-1 overflow-y-auto gray-small-scroll px-6 py-4 space-y-6">
              {/* Info section */}
              <div>
                {infoRow("Прималац", log.to)}
                {infoRow("Пошиљалац", log.from)}
                {infoRow("Наслов", log.subject)}
                {infoRow(
                  "Статус",
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      log.status === "success"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {log.status === "success" ? "Успешно" : "Неуспешно"}
                  </span>
                )}
                {infoRow("Сврха", PURPOSE_LABELS[log.purpose] || log.purpose)}
                {infoRow("Послато", formatDateTime(log.sentAt))}
                {log.sentBy != null && infoRow("Послао (ID корисника)", log.sentBy)}

                {/* Error message */}
                {log.status === "failed" && log.errorMessage && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <span className="font-semibold text-red-700 text-sm">Грешка:</span>
                    <p className="text-red-600 text-sm mt-1 break-all">{log.errorMessage}</p>
                  </div>
                )}

                {/* SMTP server info */}
                {log.smtpServer && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <span className="font-semibold text-blue-700 text-sm">СМТП сервер:</span>
                    <div className="text-blue-700 text-sm mt-1">
                      {log.smtpServer.name} ({log.smtpServer.host})
                    </div>
                  </div>
                )}

                {/* Resent from */}
                {log.resentFrom && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <span className="font-semibold text-yellow-800 text-sm">
                      Поново послато од:
                    </span>
                    <div className="text-yellow-800 text-sm mt-1">
                      <div>
                        <strong>ID:</strong> {log.resentFrom.id}
                      </div>
                      <div>
                        <strong>Прималац:</strong> {log.resentFrom.to}
                      </div>
                      <div>
                        <strong>Наслов:</strong> {log.resentFrom.subject}
                      </div>
                      <div>
                        <strong>Послато:</strong> {formatDateTime(log.resentFrom.sentAt)}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* HTML Body Preview */}
              {log.body && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 mb-2">Садржај поруке</h3>
                  <div className="border border-[#81848f3d] rounded-lg overflow-hidden bg-white">
                    <iframe
                      title="Преглед е-поруке"
                      srcDoc={log.body}
                      className="w-full border-0"
                      style={{ height: 400 }}
                      sandbox=""
                    />
                  </div>
                </div>
              )}

              {/* Resend button */}
              <div className="flex justify-end pt-2 pb-4">
                <button
                  onClick={() => setResendConfirmOpen(true)}
                  disabled={resending}
                  className="bg-[#04438b] hover:opacity-90 text-white font-semibold py-2 px-6 rounded-lg transition-opacity disabled:opacity-50"
                >
                  Поново пошаљи
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center py-16 text-gray-500">
              Није могуће учитати податке.
            </div>
          )}
        </div>
      </Modal>

      {/* Resend Confirmation */}
      <ConfirmationModal
        modalIsOpen={resendConfirmOpen}
        closeModal={() => setResendConfirmOpen(false)}
        confirmation={handleResend}
        message="Поново послати ову е-поруку?"
        subMessage={log ? `Прималац: ${log.to}\nНаслов: ${log.subject}` : ""}
        isConfirmDisabled={resending}
      />
    </>
  );
};

export default EmailLogDetailModal;
